import { useDeleteFavoritesProductMutation } from '@graphql/mutations/__generated__/me.generated'
import {
  useMeFavoritesProductsLazyQuery,
  useMeFavoritesProductsQuery,
} from '@graphql/queries/__generated__/me.generated'
import { Divider } from '@mui/material'
import Button from '@mui/material/Button'
import { useSnackbar } from 'notistack'
import React, { useEffect, useState } from 'react'
import Loader from '@components/Loader'
import { Link } from 'react-router-dom'
import { getProductUrl, PROFILE_FAVORITES_URL } from '../../../urls'
import ProductDetailsDialog from '../../Product/ProductDetailsDialog'
import { Trans, useTranslation } from 'react-i18next'
import FormattedPrice from '@components/FormattedPrice'

const SHOW_ITEMS = 3

interface HeaderFavoritesProps {
  isHover: boolean
}

const HeaderFavorites: React.FC<HeaderFavoritesProps> = ({ isHover }) => {
  const { t } = useTranslation()
  const { enqueueSnackbar } = useSnackbar()
  const [loadingLines, setLoadingLines] = useState([])
  const [activeProductData, setActiveProductData] = useState<{
    pk: string | number
    slug: string
  } | null>(null)
  const [loadFavorites, favoritesQueryResult] = useMeFavoritesProductsLazyQuery({
    variables: {
      limit: 3,
      offset: 0,
    },
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
  })
  useEffect(() => {
    if (isHover && !favoritesQueryResult.data) {
      loadFavorites()
    }
  }, [isHover, favoritesQueryResult.data])

  const [removeFavorite] = useDeleteFavoritesProductMutation({
    refetchQueries: ['MeFavoritesProducts', 'MeFavoritesReflections', 'MeFavoritesCategoriesInfo'],
    awaitRefetchQueries: true,
  })

  const products = favoritesQueryResult.data?.me?.wishedProducts.results || []

  const handleRemoveFavorite = productPk => async () => {
    setLoadingLines(prev => [...prev, productPk])
    try {
      await removeFavorite({
        variables: {
          pk: productPk,
        },
      })
      enqueueSnackbar(
        t('snackbarMessages.error.productRemovedFromFavorites', 'Product has been removed from favorites.'),
        { variant: 'error' }
      )
    } catch (e) {
      enqueueSnackbar(e.message, { variant: 'error' })
    }
    setLoadingLines(prev => prev.filter(pk => pk !== productPk))
  }
  const handleClickProduct = spec => e => {
    e.preventDefault()
    setActiveProductData({ pk: spec.pk, slug: spec.slug })
  }
  const totalCount = favoritesQueryResult.data?.me?.wishedProducts.totalCount || 0
  // not-yet-called lazy query reports loading: false - don't flash "empty" before the first fetch
  const isLoading = !favoritesQueryResult.called || favoritesQueryResult.loading
  return (
    <>
      {!products.length && !isLoading && (
        <>
          <div className='px-12 py-20 text-center text-lg font-bold'>
            {t('yourFavoritesIsEmpty', 'Your Favorites is empty.')}
          </div>
          <Divider />
        </>
      )}
      {!products.length && isLoading && (
        <div className='px-12 py-20'>
          <Loader className='spinner spinner--small' type='ThreeDots' color='#333' visible height={100} width={100} />
        </div>
      )}
      {products.length > 0 && (
        <ul className='p-0 m-0 list-none overflow-auto text-sm'>
          {products.map(product => {
            const spec = product.specifics[0]
            const price_min = product.priceRange.discount.min
            const price_max = product.priceRange.discount.max
            return (
              <li key={product.id} className='p-12 flex w-full border-0 border-b border-gray-400 border-solid relative'>
                <Loader
                  className='spinner spinner--small'
                  type='ThreeDots'
                  color='#333'
                  visible={loadingLines.includes(product.pk)}
                  height={100}
                  width={100}
                />
                <Link to={getProductUrl(spec.slug, spec.pk)} onClick={handleClickProduct(spec)}>
                  <img className='w-90 mr-10 max-w-full' src={product.thumbnail.md} alt={spec.name} />
                </Link>
                <div className='flex-1'>
                  <div>
                    <Link
                      className='no-underline hover:underline line-clamp-2 pb-4'
                      to={getProductUrl(spec.slug, spec.pk)}
                      onClick={handleClickProduct(spec)}
                    >
                      {spec.name}
                    </Link>
                  </div>
                  <p className='font-bold' itemProp='offers' itemType='http:schema.org/Offer'>
                    <span>
                      <span>
                        {price_min === price_max && (
                          <span className='currency data-hj-allow'>
                            <FormattedPrice currency={product.priceRange.currency} amount={price_min} />
                          </span>
                        )}
                        {price_min !== price_max && (
                          <span className='currency data-hj-allow'>
                            <FormattedPrice currency={product.priceRange.currency} amount={price_min} /> -{' '}
                            <FormattedPrice currency={product.priceRange.currency} amount={price_max} />
                          </span>
                        )}
                      </span>
                    </span>
                  </p>
                  <div className='flex items-center -ml-10'>
                    <Button
                      className='transparent'
                      type='button'
                      size='small'
                      onClick={handleRemoveFavorite(product.pk)}
                      disableRipple
                      disableElevation
                    >
                      <span className='underline text-12 normal-case'>{t('button.remove', 'Remove')}</span>
                    </Button>
                  </div>
                </div>
              </li>
            )
          })}
        </ul>
      )}
      {totalCount - SHOW_ITEMS > 0 && (
        <>
          <div className='p-12 flex items-center font-bold justify-center text-center'>
            <span>
              <Trans
                i18nKey='header.favoritesCountDescription'
                defaults={`There are {{itemsCount}} more items in your <a>favorites</a>`}
                values={{ itemsCount: totalCount - SHOW_ITEMS }}
                count={totalCount - SHOW_ITEMS}
                components={{
                  a: <Link to={PROFILE_FAVORITES_URL} className='underline' />,
                }}
              />
            </span>
          </div>
          <Divider />
        </>
      )}
      <div className='p-12'>
        <Button
          component={Link}
          to={PROFILE_FAVORITES_URL}
          variant='contained'
          color='secondary'
          size='large'
          fullWidth
          disableElevation
        >
          <span className='font-bold tracking-1'>{t('button.favorites', 'Favorites')}</span>
        </Button>
      </div>
      <ProductDetailsDialog isOpen={!!activeProductData} setIsOpen={setActiveProductData} spec={activeProductData} />
    </>
  )
}

export default HeaderFavorites
